import { useState } from 'react';
import { Button } from '../button';
import { TextEditorView, type ViewerSourceEditing } from './TextEditorView';

// Injected ahead of the document so previewed HTML cannot fetch external
// subresources: no scripts, no network, no forms — inline styles and data-URI
// images still render. A content-supplied CSP meta combines by intersection
// (safe); injecting before an existing <!DOCTYPE> puts the frame in quirks
// mode (cosmetic only, accepted).
const CSP_PREFIX =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data:; style-src \'unsafe-inline\'">';

type ViewMode = 'preview' | 'source';

/**
 * Sandboxed HTML preview with a source toggle. The preview iframe carries an
 * EMPTY sandbox token list (no scripts, opaque origin) plus the CSP prefix
 * above; the source side is the CM6 editor.
 *
 * With `editing`: `content` is the buffer at SYNC
 * POINTS (the pane only re-renders it at debounce/save boundaries, so the
 * iframe's srcDoc never churns per keystroke), the source editor carries the
 * host's buffer wiring, and dirty pins the toggle to source with a "save to
 * preview" hint.
 */
export function HtmlView({
  content,
  filename,
  editing,
}: {
  content: string;
  filename: string;
  editing?: ViewerSourceEditing;
}) {
  const [mode, setMode] = useState<ViewMode>('preview');
  // Dirty pins the toggle to source — the preview would silently show
  // stale (or half-synced) markup while edits live in the editor.
  const pinnedToSource = editing?.dirty === true;
  const effectiveMode: ViewMode = pinnedToSource ? 'source' : mode;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-md text-sm">
      <div className="flex items-center justify-end gap-1 border-b px-3 py-1.5">
        {pinnedToSource && (
          <span className="mr-auto text-xs text-muted-foreground" data-testid="save-to-preview-hint">
            Save to preview
          </span>
        )}
        <Button
          type="button"
          variant={effectiveMode === 'preview' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setMode('preview')}
          disabled={pinnedToSource}
          className="h-7 px-2 text-xs"
        >
          Preview
        </Button>
        <Button
          type="button"
          variant={effectiveMode === 'source' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setMode('source')}
          className="h-7 px-2 text-xs"
        >
          Source
        </Button>
      </div>
      {effectiveMode === 'preview' ? (
        <iframe
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={CSP_PREFIX + content}
          title={filename}
          className="min-h-0 w-full flex-1 border-0 bg-white"
        />
      ) : (
        <div className="min-h-0 flex-1 p-1">
          <TextEditorView
            content={content}
            filename={filename}
            readOnly={editing ? editing.readOnly : true}
            onDocChanged={editing?.onDocChanged}
            getTextRef={editing?.getTextRef}
            onSave={editing?.onSave}
          />
        </div>
      )}
    </div>
  );
}
